import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, Check, CheckCheck, MessageSquarePlus, MapPin, ExternalLink, Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionRibbon } from "@/components/AppShell";
import UserActionsMenu from "@/components/UserActionsMenu";
import { toast } from "sonner";
import useUserSearch from "@/hooks/useUserSearch";
import UserMatchCard from "@/components/UserMatchCard";

const MSG_TESTIDS = {
  page: "messages-page",
  threadList: "messages-thread-list",
  thread: (id) => `messages-thread-${id}`,
  chatView: "messages-chat-view",
  bubble: (id) => `messages-bubble-${id}`,
  input: "messages-input",
  sendBtn: "messages-send-btn",
  backBtn: "messages-back-btn",
  readReceipt: (id) => `messages-read-receipt-${id}`,
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function ThreadList({ threads, onOpen }) {
  if (!threads.length) {
    return (
      <div className="text-center py-16 px-6">
        <p className="font-serif-it text-tb-navy text-2xl">no messages yet</p>
        <p className="font-hand text-tb-navy/60 mt-2">
          open the Lounge, find a traveler whose story you love,
          and tap "message" to start a conversation.
        </p>
      </div>
    );
  }
  return (
    <ul data-testid={MSG_TESTIDS.threadList} className="px-3 py-2 space-y-1">
      {threads.map((t) => (
        <li key={t.thread_id}>
          <button
            data-testid={MSG_TESTIDS.thread(t.peer.id)}
            onClick={() => onOpen(t.peer.id)}
            className={
              "w-full text-left rounded-2xl px-3 py-2.5 flex items-center gap-3 tb-tap border-2 " +
              (t.unread > 0
                ? "bg-tb-cream-2 border-tb-gold/60"
                : "bg-tb-cream border-tb-cream-3")
            }
          >
            <img
              src={t.peer.avatar}
              alt=""
              className="w-12 h-12 rounded-full bg-tb-cream-3 object-cover shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-serif-it text-tb-navy text-lg truncate">{t.peer.name}</span>
                <span className="font-hand text-tb-navy/55 text-xs shrink-0">{timeAgo(t.last_at)}</span>
              </div>
              <div className="font-ui text-sm text-tb-navy/75 truncate">{t.last_message}</div>
            </div>
            {t.unread > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-tb-coral text-white text-xs font-ui font-bold flex items-center justify-center">
                {t.unread}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

function DestinationCard({ d, mine, onSaved }) {
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await api.post(`/destinations/${d.id}/swipe`, { direction: "right" });
      toast.success(`${d.name} saved to your bookmarks`);
      onSaved?.(d.id);
    } catch {
      toast.error("Couldn't save that one");
    } finally { setSaving(false); }
  };
  const openAffiliate = (e) => {
    e.stopPropagation();
    if (d.affiliate_url) {
      window.open(d.affiliate_url, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <div
      data-testid={`msg-dest-card-${d.id}`}
      className={
        "mt-1.5 rounded-xl overflow-hidden border-2 w-[240px] " +
        (mine ? "border-tb-gold/40 bg-tb-navy-soft" : "border-tb-cream-3 bg-tb-cream")
      }
    >
      {d.image && <img src={d.image} alt={d.name} className="w-full h-24 object-cover" />}
      <div className="px-2.5 py-2">
        <div className={"font-serif-it text-base leading-tight " + (mine ? "text-tb-gold-bright" : "text-tb-navy")}>
          {d.name}
        </div>
        <div className={"font-hand text-xs flex items-center gap-1 mt-0.5 " + (mine ? "text-tb-gold/85" : "text-tb-navy/60")}>
          <MapPin className="w-3 h-3" /> {d.country}
        </div>
        <div className="flex items-center gap-1 mt-2">
          <button
            data-testid={`msg-dest-save-${d.id}`}
            disabled={saving}
            onClick={save}
            className="flex-1 px-2 py-1 rounded-full bg-tb-coral text-white font-hand text-xs tb-tap disabled:opacity-50 flex items-center justify-center gap-1"
          >
            <Bookmark className="w-3 h-3" /> save
          </button>
          {d.affiliate_url && (
            <button
              data-testid={`msg-dest-open-${d.id}`}
              onClick={openAffiliate}
              className="flex-1 px-2 py-1 rounded-full bg-tb-cream-2 border border-tb-cream-3 text-tb-navy font-hand text-xs tb-tap flex items-center justify-center gap-1 hover:border-tb-blue"
            >
              open <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatView({ peerId, currentUserId, onBack }) {
  const [data, setData] = useState({ messages: [], peer: null });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/with/${peerId}`);
      setData(data);
      // Mark everything from this peer as read once we open the thread.
      api.post(`/messages/with/${peerId}/read`).catch(() => {});
    } catch (err) {
      console.error("[Messages] load thread failed:", err);
    }
  }, [peerId]);

  useEffect(() => { load(); }, [load]);

  // Light polling so read receipts arrive in near-real time.
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [data.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    // optimistic
    const optimistic = {
      id: "tmp-" + Date.now(),
      sender_id: currentUserId,
      recipient_id: peerId,
      text: t,
      read: false,
      read_at: null,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setData((d) => ({ ...d, messages: [...d.messages, optimistic] }));
    setText("");
    try {
      const { data: saved } = await api.post("/messages", { recipient_id: peerId, text: t });
      setData((d) => ({
        ...d,
        messages: d.messages.map((m) => (m.id === optimistic.id ? saved : m)),
      }));
    } catch {
      setData((d) => ({ ...d, messages: d.messages.filter((m) => m.id !== optimistic.id) }));
      toast.error("Couldn't send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={MSG_TESTIDS.chatView} className="flex flex-col h-full">
      <div className="px-3 py-2 flex items-center gap-3 border-b border-tb-navy/10 bg-white shrink-0">
        {/* 2026-07-17 Fix 5 — single back button routing to /lounge
            (where the user came from). Previously the ChatView back
            popped the search-params and left the user on /messages
            (thread list) which duplicated the AppShell's back
            affordance and read as "two back arrows". Now `onBack`
            navigates all the way to /lounge for a clean single-tap
            return. Label: `Lounge` (was implicit "Conversation"). */}
        <button
          onClick={onBack}
          data-testid="chat-view-back-to-lounge"
          aria-label="Back to Lounge"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 hover:bg-tb-navy/5 tb-tap font-hand text-tb-navy text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> <span>Lounge</span>
        </button>
        {data.peer && (
          <>
            <UserActionsMenu
              targetUserId={data.peer.id}
              targetName={data.peer.name}
              targetAvatar={data.peer.avatar}
              currentUserId={currentUserId}
              contextLabel="conversation"
              contextType="user"
              contextId={data.peer.id}
              onBlocked={onBack}
              onMessage={() => {}}
              imgClassName="w-9 h-9 rounded-full bg-tb-cream-3 object-cover"
              testIdPrefix={`messages-peer-avatar`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-serif-it text-tb-navy text-lg truncate leading-tight">{data.peer.name}</div>
              <div className="font-hand text-tb-navy/55 text-xs truncate">@{data.peer.username} · tap avatar for options</div>
            </div>
          </>
        )}
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
        {data.messages.length === 0 ? (
          <p className="font-hand text-tb-navy/55 italic text-center mt-10">
            say hi — your conversation starts here
          </p>
        ) : (
          data.messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            const dests = m.destinations || [];
            const hasOnlyDests = dests.length > 0 && !m.text;
            return (
              <div
                key={m.id}
                data-testid={MSG_TESTIDS.bubble(m.id)}
                className={"flex " + (mine ? "justify-end" : "justify-start")}
              >
                <div
                  className={
                    "max-w-[78%] rounded-2xl px-3 py-1.5 " +
                    (mine
                      ? "bg-tb-navy text-tb-gold-bright rounded-br-md"
                      : "bg-tb-cream-2 text-tb-navy rounded-bl-md border border-tb-cream-3")
                  }
                >
                  {m.text && (
                    <div className="font-ui text-sm whitespace-pre-wrap">{m.text}</div>
                  )}
                  {dests.length > 0 && (
                    <div className={"flex flex-wrap gap-2 " + (hasOnlyDests ? "" : "mt-1")}>
                      {dests.map((d) => (
                        <DestinationCard key={d.id} d={d} mine={mine} />
                      ))}
                    </div>
                  )}
                  <div className={"flex items-center gap-1 mt-0.5 text-[10px] " +
                    (mine ? "text-tb-gold/70 justify-end" : "text-tb-navy/50")}>
                    <span>{timeAgo(m.created_at)}</span>
                    {mine && (
                      <span data-testid={MSG_TESTIDS.readReceipt(m.id)}>
                        {m.read ? <CheckCheck className="w-3.5 h-3.5 text-tb-gold-bright" />
                                : <Check className="w-3.5 h-3.5" />}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="px-3 py-2 border-t border-tb-cream-3 bg-tb-cream flex items-center gap-2 shrink-0">
        <input
          data-testid={MSG_TESTIDS.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="write a message…"
          className="flex-1 px-3 py-2 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
        />
        <button
          data-testid={MSG_TESTIDS.sendBtn}
          disabled={sending || !text.trim()}
          className="w-10 h-10 rounded-full bg-tb-navy text-tb-gold-bright flex items-center justify-center tb-tap disabled:opacity-50"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const peerId = params.get("with");

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data.threads || []);
    } catch (err) {
      console.error("[Messages] load threads failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!peerId) loadThreads(); }, [peerId, loadThreads]);

  if (peerId && user) {
    return (
      <ChatView
        peerId={peerId}
        currentUserId={user.id}
        onBack={() => nav("/lounge")}
      />
    );
  }

  return (
    <div data-testid={MSG_TESTIDS.page} className="flex flex-col">
      <SectionRibbon>your messages</SectionRibbon>

      <div className="px-5 pt-3 pb-2 flex items-center justify-between">
        <button
          data-testid={MSG_TESTIDS.backBtn}
          onClick={() => nav(-1)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/15 font-hand text-tb-navy tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>
        <button
          onClick={() => nav("/lounge")}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap"
        >
          <MessageSquarePlus className="w-4 h-4" /> find travelers
        </button>
      </div>

      {loading ? (
        <div className="font-hand text-tb-navy/60 text-xl text-center py-12">~ checking your inbox ~</div>
      ) : (
        <>
          <MessagesUserSearch />
          <ThreadList threads={threads} onOpen={(id) => setParams({ with: id })} />
        </>
      )}
    </div>
  );
}

// Item 2 — search users from within the Messages tab.
function MessagesUserSearch() {
  const [q, setQ] = React.useState("");
  const { users } = useUserSearch(q, { enabled: q.trim().length >= 2, limit: 8 });
  return (
    <div className="px-5 pb-3">
      <input
        data-testid="messages-user-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="find travelers by name or @handle"
        className="w-full px-3 py-2 rounded-full bg-white border-2 border-tb-navy/15 focus:border-tb-navy outline-none font-ui text-sm"
      />
      {users.length > 0 && (
        <div className="mt-2" data-testid="messages-people-section">
          <div className="mb-1.5 px-0.5">
            <span className="font-hand text-tb-navy/70 text-xs uppercase tracking-wide">People</span>
          </div>
          <ul className="space-y-2">
            {users.map((u) => (
              <li key={u.id}><UserMatchCard user={u} /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
